import React, { useRef } from 'react';
import {
  Box,
  Drawer,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { DOCUMENT_ACCEPT } from '../../utils/documentFileTypes';

interface AttachTile {
  key: string;
  label: string;
  icon: React.ReactNode;
  circleBg: string;
  iconColor: string;
  onClick: () => void;
}

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onGallerySelect: (files: FileList) => void;
  onOpenCamera: () => void;
  onDocumentSelect: (file: File) => void;
  onLocationSelect: () => void;
  locationLoading?: boolean;
}

function AttachGrid({ tiles }: { tiles: AttachTile[] }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 2,
        p: 2,
        pb: 3,
      }}
    >
      {tiles.map((tile) => (
        <Box
          key={tile.key}
          onClick={tile.onClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: tile.circleBg,
              color: tile.iconColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {tile.icon}
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#5f6368', textAlign: 'center' }}>
            {tile.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export const CommunityAttachMenu: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  onGallerySelect,
  onOpenCamera,
  onDocumentSelect,
  onLocationSelect,
  locationLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const galleryRef = useRef<HTMLInputElement>(null);
  const documentRef = useRef<HTMLInputElement>(null);

  const tiles: AttachTile[] = [
    {
      key: 'gallery',
      label: 'Photos & videos',
      icon: <PhotoLibraryIcon />,
      circleBg: '#F3E5F5',
      iconColor: '#9C27B0',
      onClick: () => galleryRef.current?.click(),
    },
    {
      key: 'camera',
      label: 'Camera',
      icon: <PhotoCameraIcon />,
      circleBg: '#E3F2FD',
      iconColor: '#2196F3',
      onClick: () => {
        onClose();
        onOpenCamera();
      },
    },
    {
      key: 'document',
      label: 'Document',
      icon: <InsertDriveFileIcon />,
      circleBg: '#E8EAF6',
      iconColor: '#3F51B5',
      onClick: () => documentRef.current?.click(),
    },
    {
      key: 'location',
      label: locationLoading ? 'Getting location…' : 'Location',
      icon: <LocationOnIcon />,
      circleBg: '#FFEBEE',
      iconColor: '#F44336',
      onClick: () => {
        onClose();
        onLocationSelect();
      },
    },
  ];

  const hiddenInputs = (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) {
            onGallerySelect(e.target.files);
            onClose();
          }
          e.target.value = '';
        }}
      />
      <input
        ref={documentRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onDocumentSelect(file);
            onClose();
          }
          e.target.value = '';
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        {hiddenInputs}
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
          }}
        >
          <Box sx={{ width: '100%', pt: 1 }}>
            <Box sx={{ width: 36, height: 4, bgcolor: '#dadce0', borderRadius: 2, mx: 'auto', mb: 1 }} />
            <AttachGrid tiles={tiles} />
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <>
      {hiddenInputs}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 2, mt: -1 } }}
      >
        <AttachGrid tiles={tiles} />
      </Popover>
    </>
  );
};
